import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { formatDateForDisplay } from '@/lib/utils';

export interface TemplateField {
    key: string;
    label: string;
    type: 'text' | 'textarea' | 'select' | 'checkbox' | 'user_picker' | 'date' | 'date_range';
    required?: boolean;
    placeholder?: string;
    help_text?: string;
    options?: string[];
    order?: number;
}

export interface UserOption {
    id: string;
    name: string;
    email?: string;
}

const NONE = '_none';

interface DynamicRequestFormProps {
    fields: TemplateField[];
    data: Record<string, unknown>;
    onChange: (key: string, value: unknown) => void;
    errors?: Record<string, string>;
    users?: UserOption[];
    readOnly?: boolean;
}

export function DynamicRequestForm({ fields, data, onChange, errors, users = [], readOnly = false }: DynamicRequestFormProps) {
    const { t } = useTranslations();
    const sorted = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const displayValue = (field: TemplateField, value: unknown): string => {
        const empty = '—';
        switch (field.type) {
            case 'checkbox':
                return value ? t('service_requests.form.yes', 'Yes') : t('service_requests.form.no', 'No');
            case 'user_picker':
                return users.find((u) => u.id === value)?.name ?? (value ? String(value) : empty);
            case 'date':
                return value ? formatDateForDisplay(String(value)) : empty;
            case 'date_range': {
                const range = (value ?? {}) as { from?: string; to?: string };
                if (!range.from && !range.to) return empty;
                return `${range.from ? formatDateForDisplay(range.from) : empty} – ${range.to ? formatDateForDisplay(range.to) : empty}`;
            }
            default:
                return value !== undefined && value !== null && String(value) !== '' ? String(value) : empty;
        }
    };

    return (
        <div className="space-y-5">
            {sorted.map((field) => {
                const value = data[field.key];
                const error = errors?.[`submitted_data.${field.key}`] ?? errors?.[field.key];

                // Read-only mode renders a clean labelled value instead of disabled
                // inputs — this is what the show pages use.
                if (readOnly) {
                    return (
                        <div key={field.key} className="space-y-1">
                            <Label className="text-muted-foreground text-xs font-medium">{field.label}</Label>
                            <p className="text-sm font-medium break-words whitespace-pre-wrap">{displayValue(field, value)}</p>
                        </div>
                    );
                }

                return (
                    <div key={field.key} className="space-y-1.5">
                        {field.type !== 'checkbox' && (
                            <Label htmlFor={field.key} className="text-sm font-medium">
                                {field.label}
                                {field.required && <span className="text-destructive ml-1">*</span>}
                            </Label>
                        )}

                        {field.type === 'text' && (
                            <Input
                                id={field.key}
                                value={String(value ?? '')}
                                onChange={(e) => onChange(field.key, e.target.value)}
                                placeholder={field.placeholder}
                                className={error ? 'border-destructive' : ''}
                            />
                        )}

                        {field.type === 'textarea' && (
                            <Textarea
                                id={field.key}
                                value={String(value ?? '')}
                                onChange={(e) => onChange(field.key, e.target.value)}
                                placeholder={field.placeholder}
                                rows={4}
                                className={error ? 'border-destructive' : ''}
                            />
                        )}

                        {field.type === 'select' && (
                            <Select value={String(value ?? '')} onValueChange={(v) => onChange(field.key, v)}>
                                <SelectTrigger className={`w-full ${error ? 'border-destructive' : ''}`}>
                                    <SelectValue placeholder={field.placeholder ?? t('service_requests.form.select_option', 'Select an option')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {(field.options ?? []).map((opt) => (
                                        <SelectItem key={opt} value={opt}>
                                            {opt}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}

                        {field.type === 'checkbox' && (
                            <div className="flex items-center gap-2">
                                <Checkbox id={field.key} checked={Boolean(value)} onCheckedChange={(checked) => onChange(field.key, checked)} />
                                <Label htmlFor={field.key} className="cursor-pointer text-sm font-medium">
                                    {field.label}
                                    {field.required && <span className="text-destructive ml-1">*</span>}
                                </Label>
                            </div>
                        )}

                        {field.type === 'user_picker' && (
                            <Select value={String(value ?? '')} onValueChange={(v) => onChange(field.key, v === NONE ? '' : v)}>
                                <SelectTrigger className={`w-full ${error ? 'border-destructive' : ''}`}>
                                    <SelectValue placeholder={field.placeholder ?? t('service_requests.form.select_user', 'Select user')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {!field.required && <SelectItem value={NONE}>{t('service_requests.form.none', '— None —')}</SelectItem>}
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.name}
                                            {u.email ? ` (${u.email})` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}

                        {field.type === 'date' && (
                            <DatePicker
                                value={String(value ?? '')}
                                onValueChange={(v) => onChange(field.key, v)}
                                placeholder={field.placeholder ?? t('service_requests.form.pick_date', 'Pick a date')}
                                className={error ? 'border-destructive' : ''}
                            />
                        )}

                        {field.type === 'date_range' && (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <DatePicker
                                    value={String((value as { from?: string })?.from ?? '')}
                                    onValueChange={(v) => onChange(field.key, { ...(value as object), from: v })}
                                    placeholder={t('service_requests.form.from', 'From')}
                                />
                                <DatePicker
                                    value={String((value as { to?: string })?.to ?? '')}
                                    onValueChange={(v) => onChange(field.key, { ...(value as object), to: v })}
                                    placeholder={t('service_requests.form.to', 'To')}
                                />
                            </div>
                        )}

                        {field.help_text && !error && <p className="text-muted-foreground text-xs">{field.help_text}</p>}
                        {error && <p className="text-destructive text-xs">{error}</p>}
                    </div>
                );
            })}
        </div>
    );
}
